import { createContext, useContext, useEffect, useMemo, useState } from 'react';

/**
 * The environment a bundle is running in is a property of THE ORIGIN IT WAS
 * SERVED FROM, never of client state. It is read once from `GET /api/environment`
 * (api/routes/index.js — ungated, mounted above `environmentAuth`).
 *
 * Deliberately NOT localStorage and NOT a query param: either would let a copied
 * URL or a stale tab relabel a LIVE page as SANDBOX, which is the exact failure
 * this whole workstream exists to prevent. A new tab inherits the origin, so it
 * inherits the environment for free.
 */
const EnvironmentContext = createContext(null);

const FALLBACK_DESCRIPTOR = Object.freeze({
  env: 'live',
  label: 'LIVE',
  isSandbox: false,
  appUrl: '',
  peerUrl: null,
  release: Object.freeze({ commit: 'unknown', builtAt: 'unknown', version: 'unknown' })
});

/**
 * Fail CLOSED to LIVE. An unreachable descriptor must never render a live page
 * as sandbox — a false "SANDBOX" badge invites destructive testing on real data.
 */
const fetchDescriptor = async () => {
  try {
    const response = await fetch('/api/environment', { headers: { Accept: 'application/json' } });
    if (!response.ok) return FALLBACK_DESCRIPTOR;
    const body = await response.json();
    return { ...FALLBACK_DESCRIPTOR, ...body, release: { ...FALLBACK_DESCRIPTOR.release, ...(body?.release ?? {}) } };
  } catch {
    return FALLBACK_DESCRIPTOR;
  }
};

export const EnvironmentProvider = ({ children }) => {
  const [descriptor, setDescriptor] = useState(FALLBACK_DESCRIPTOR);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isActive = true;

    const loadDescriptor = async () => {
      const next = await fetchDescriptor();
      if (!isActive) return;
      setDescriptor(next);
      setIsLoading(false);
    };

    loadDescriptor();
    return () => { isActive = false; };
  }, []);

  const value = useMemo(() => ({ ...descriptor, isLoading }), [descriptor, isLoading]);

  return <EnvironmentContext.Provider value={value}>{children}</EnvironmentContext.Provider>;
};

export const useEnvironment = () => useContext(EnvironmentContext) ?? { ...FALLBACK_DESCRIPTOR, isLoading: false };

export default EnvironmentContext;
